import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Plus } from 'lucide-react';

import ScoringModelFilters from '../../components/credit/scoring/ScoringModelFilters';
import ScoringModelTable from '../../components/credit/scoring/ScoringModelTable';
import DeleteConfirmDialog from '../../components/credit/scoring/DeleteConfirmDialog';
import apiClient from '../../api/apiClient';
import { useToast } from '../../context/ToastContext';

interface ScoringModel {
  id: number;
  title: string;
  credit_type: string;
  debtor_type: string;
  is_entrepreneur: boolean;
  status: string;
  is_default: boolean;
  last_updated: string;
}

export default function ScoringModelList() {
  const { showError } = useToast();
  const [models, setModels] = useState<ScoringModel[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterCreditType, setFilterCreditType] = useState('all');
  const [deleteId, setDeleteId] = useState<number | null>(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiClient.get<ScoringModel[]>('/credit/scoring');
      setModels(data || []);
    } catch (error) {
      console.error('Error loading data:', error);
      showError('Gagal memuat data model scoring');
    } finally {
      setLoading(false);
    }
  }, [showError]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await apiClient.delete(`/credit/scoring/${deleteId}`);
      await loadData();
      setDeleteId(null);
    } catch (error: any) {
      console.error('Error deleting model:', error);
      showError(error?.message || 'Gagal menghapus model scoring');
    }
  };

  const handleToggleStatus = async (model: ScoringModel) => {
    try {
      await apiClient.patch(`/credit/scoring/${model.id}/toggle-status`, {});
      await loadData();
    } catch (error: any) {
      console.error('Error toggling status:', error);
      showError(error?.message || 'Gagal mengubah status model');
    }
  };

  const handleDuplicate = async (model: ScoringModel) => {
    try {
      // TODO: Replace with actual API call
      loadData();
    } catch (error) {
      console.error('Error duplicating model:', error);
    }
  };

  const handleSetDefault = async (model: ScoringModel) => {
    try {
      await apiClient.patch(`/credit/scoring/${model.id}/set-default`, {});
      await loadData();
    } catch (error: any) {
      console.error('Error setting default:', error);
      showError(error?.message || 'Gagal mengatur model sebagai default');
    }
  };

  const filteredModels = models.filter(model => {
    const matchSearch = model.title?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchStatus = filterStatus === 'all' || model.status === filterStatus;
    const matchCreditType = filterCreditType === 'all' || model.credit_type === filterCreditType;
    return matchSearch && matchStatus && matchCreditType;
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900">
            Manajemen Model Scoring Kredit
          </h1>
          <p className="text-gray-500 mt-1">Kelola model penilaian kelayakan kredit berbasis 5C</p>
        </div>
        <Link to="/credit/scoring/new">
          <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all duration-300 flex items-center gap-2">
            <Plus className="w-4 h-4" />
            Tambah Model Scoring
          </button>
        </Link>
      </div>

      {/* Filters */}
      <ScoringModelFilters
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        filterStatus={filterStatus}
        onStatusChange={setFilterStatus}
        filterCreditType={filterCreditType}
        onCreditTypeChange={setFilterCreditType}
      />

      {/* Table */}
      <div className="bg-white border rounded-lg">
        <ScoringModelTable
          models={filteredModels}
          loading={loading}
          onDelete={setDeleteId}
          onDuplicate={handleDuplicate}
          onToggleStatus={handleToggleStatus}
          onSetDefault={handleSetDefault}
        />
      </div>

      {/* Delete Confirmation */}
      <DeleteConfirmDialog
        isOpen={!!deleteId}
        onClose={() => setDeleteId(null)}
        onConfirm={handleDelete}
      />
    </div>
  );
}
